import { CalendarOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CONSTANT } from '@salescore/client-base'
import { CORE_CONSTANT, type ViewConfigDimension, type ViewTimeframe } from '@salescore/core'
import { Button, List, Select } from 'antd'
import { arrayMoveImmutable } from 'array-move'
import { Array, Order, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useMemo, useState } from 'react'

// i18n:グローバル変数のため別途対応
const dateTimeOptions: Array<{ value: ViewTimeframe; label: string }> = [
  {
    value: 'year',
    label: t(`年`),
  },
  {
    value: 'fiscal_year',
    label: t(`会計年`),
  },
  {
    value: 'fiscal_half',
    label: t(`半期`),
  },
  {
    value: 'fiscal_quarter',
    label: t(`Q`),
  },
  {
    value: 'month',
    label: t(`月`),
  },
  {
    value: 'week',
    label: t(`週`),
  },
  {
    value: 'weekday',
    label: t(`曜日`),
  },
  {
    value: 'day',
    label: t(`日`),
  },
  {
    value: 'hour',
    label: t(`時間`),
  },
]

interface GroupedDimension {
  dimension: ViewConfigDimension
  isDateRelated: boolean
  dateSpans: Array<Exclude<ViewConfigDimension['dateSpan'], undefined>>
}

interface SortableItemArgument {
  groupedDimension: GroupedDimension
  onDestroy: (item: GroupedDimension) => void
  onDateSpanChange: (item: GroupedDimension) => void
}

const SortableItem = ({ groupedDimension, onDestroy, onDateSpanChange }: SortableItemArgument): React.ReactNode => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: groupedDimension.dimension.key,
  })

  const deletable = groupedDimension.dimension.key !== CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key

  return (
    <div
      ref={setNodeRef}
      data-testid={`sortable-item-${groupedDimension.dimension.key}`}
      className={`flex h-12 items-center justify-between pl-3 pr-2 align-middle`}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
        borderBottom: `1px solid ${CONSTANT.colors.border}`,
      }}
      {...attributes}
      {...listeners}
    >
      <div
        style={{
          // width: 200,
          fontSize: 13,
        }}
      >
        <MenuOutlined className="pr-3 hover:opacity-75" />
        <span className="w-full text-left hover:opacity-75">{groupedDimension.dimension.label}</span>
      </div>
      <div className={`flex h-12 items-center justify-between align-middle`}>
        {groupedDimension.isDateRelated && (
          <div>
            <CalendarOutlined />
            <Select
              size="small"
              className="min-w-[130px] text-xs"
              variant="borderless"
              placeholder={t(`表示形式`)}
              tagRender={(option) => <span style={{ marginRight: 1 }}>{option.label}</span>}
              mode="multiple"
              value={groupedDimension.dateSpans}
              options={dateTimeOptions}
              onChange={(value: string[]) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                const dateSpans = value as Array<Exclude<ViewConfigDimension['dateSpan'], undefined>>
                onDateSpanChange({
                  ...groupedDimension,
                  dateSpans: pipe(
                    dateSpans,
                    Array.sortBy(
                      Order.mapInput(Order.Number, (x) => dateTimeOptions.findIndex((option) => option.value === x)),
                    ),
                  ),
                })
              }}
            />
          </div>
        )}
        {deletable && (
          <Button
            icon={<DeleteOutlined />}
            // danger
            onClick={() => {
              onDestroy(groupedDimension)
            }}
            type="text"
          />
        )}
      </div>
    </div>
  )
}

interface ListBodyArgument {
  dimensions: GroupedDimension[]
  loading: boolean
  dragging: boolean
  onDestroy: (dimension: GroupedDimension) => void
  onDateSpanChange: (item: GroupedDimension) => void
}
const SortableDimensionsListBody = ({
  dimensions,
  loading,
  dragging,
  onDestroy,
  onDateSpanChange,
}: ListBodyArgument): React.ReactNode => (
  <List
    className={`mt-4 overflow-scroll`}
    loading={loading}
    bordered={true}
    style={dragging ? { cursor: 'grabbing' } : { cursor: 'grab' }}
    dataSource={dimensions}
    renderItem={(dimension) => (
      <SortableItem
        key={`item-${dimension.dimension.key}`}
        groupedDimension={dimension}
        onDestroy={onDestroy}
        onDateSpanChange={onDateSpanChange}
      />
    )}
  />
)

function groupDimensions(dimensions: ViewConfigDimension[]): GroupedDimension[] {
  // dimensionsは、dateSpanがバラバラに入っているが、UI上はまとめる
  return pipe(
    dimensions,
    Array.groupBy((x) => x.key),
    Record.toEntries,
    Array.map(([_key, ds]): GroupedDimension => {
      const [dimension] = ds
      return {
        dimension,
        isDateRelated: dimension.fieldType === 'date' || dimension.fieldType === 'datetime',
        dateSpans: pipe(
          ds,
          Array.map((d) => d.dateSpan),
          Array.filter(isNotNullish),
          Array.sortBy(Order.mapInput(Order.Number, (x) => dateTimeOptions.findIndex((y) => y.value === x))),
        ),
      }
    }),
  )
}

function ungroupDimensions(groups: GroupedDimension[]): ViewConfigDimension[] {
  return groups.flatMap((group) => {
    if (group.dateSpans.length === 0) {
      return [group.dimension]
    }
    return group.dateSpans.map((dateSpan) => ({
      ...group.dimension,
      dateSpan,
    }))
  })
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function DimensionsPickerSortableList({
  dimensions,
  onChange,
}: {
  dimensions: ViewConfigDimension[]
  onChange: (dimensions: ViewConfigDimension[]) => void
}): React.ReactNode {
  const [dragging, setDragging] = useState(false)
  const [loading, setLoading] = useState(false)
  const groupedDimensions = useMemo(() => groupDimensions(dimensions), [dimensions])
  const itemIds: UniqueIdentifier[] = groupedDimensions.map((group) => group.dimension.key)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  )

  if (dimensions.length === 0) {
    return <></>
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => {
        setDragging(true)
      }}
      onDragEnd={({ active, over }: DragEndEvent) => {
        setDragging(false)

        if (over !== null && active.id !== over.id) {
          const oldIndex = itemIds.indexOf(active.id)
          const newIndex = itemIds.indexOf(over.id)

          // NOTE: ソート時のUXを良くするため、onChangeは非同期で行い、itemsやvalueの変更はソート直後に行われるようにした方が良い。
          const newItems = arrayMoveImmutable(groupedDimensions, oldIndex, newIndex)
          try {
            setLoading(true)
            onChange(ungroupDimensions(newItems))
          } finally {
            setLoading(false)
          }
        }
      }}
      onDragCancel={() => {
        setDragging(false)
      }}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <SortableDimensionsListBody
          dimensions={groupedDimensions}
          dragging={dragging}
          loading={loading}
          onDestroy={(dimension) => {
            const filtered = groupedDimensions.filter((x) => x.dimension.key !== dimension.dimension.key)
            onChange(ungroupDimensions(filtered))
          }}
          onDateSpanChange={(newGroupedDimension) => {
            const newGroupedDimensions = groupedDimensions.map((group) => {
              if (group.dimension.key === newGroupedDimension.dimension.key) {
                return newGroupedDimension
              }
              return group
            })
            onChange(ungroupDimensions(newGroupedDimensions))
          }}
        />
      </SortableContext>
    </DndContext>
  )
}
